/**
 * Vesper User Store (Shared / Cross-Platform)
 *
 * Uses the IStorage abstraction for token persistence.
 * Both sync (localStorage) and async (AsyncStorage) are supported.
 */

import { create } from 'zustand';
import { getStorage, resolveStorageValue } from '../storage/storage';
import { refreshToken as refreshTokenApi } from '../services/user.service';
import { bustAll as bustAllCachedFetches } from '../services/dedupedFetch';
import type { UserPublicProfile } from '../types/user.types';

/** Refresh interval: 45 minutes (well before the 1-hour JWT expiry) */
const REFRESH_INTERVAL_MS = 45 * 60 * 1000;

// Timer declarations — shared lib has no DOM/Node lib deps
declare function setInterval(callback: () => void, ms: number): number;
declare function clearInterval(id: number): void;

let refreshIntervalId: number | null = null;

function startRefreshTimer(set: (partial: Partial<AuthStore>) => void, get: () => AuthStore) {
  stopRefreshTimer();
  refreshIntervalId = setInterval(async () => {
    try {
      const newToken = await refreshTokenApi();
      const { user } = get();
      if (user) {
        const storage = getStorage();
        void Promise.resolve(storage.setItem('ark_token', newToken));
        set({ token: newToken });
      }
    } catch {
      // Refresh failed (token expired or revoked) — log out
      get().clearAuth();
    }
  }, REFRESH_INTERVAL_MS);
}

function stopRefreshTimer() {
  if (refreshIntervalId !== null) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
}

interface AuthStore {
  isAuthenticated: boolean;
  user: UserPublicProfile | null;
  token: string | null;
  isHydrated: boolean;

  hydrate: () => Promise<void>;
  setAuth: (token: string, user: UserPublicProfile) => void;
  clearAuth: () => void;
  updateUser: (updates: Partial<UserPublicProfile>) => void;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  isAuthenticated: false,
  user: null,
  token: null,
  isHydrated: false,

  hydrate: async () => {
    const storage = getStorage();
    const token = await resolveStorageValue(storage.getItem('ark_token'));
    set({
      isAuthenticated: !!token,
      token,
      isHydrated: true,
    });
    if (token) {
      startRefreshTimer(set, get);
    }
  },

  setAuth: (token, user) => {
    const storage = getStorage();
    void Promise.resolve(storage.setItem('ark_token', token));
    set({ isAuthenticated: true, token, user });
    startRefreshTimer(set, get);
  },

  clearAuth: () => {
    stopRefreshTimer();
    const storage = getStorage();
    void Promise.resolve(storage.removeItem('ark_token'));
    // Drop every cached read so the next session starts cold and can't show
    // the previous user's profile/score/bench/notification state for a TTL
    // window after login.
    bustAllCachedFetches();
    set({ isAuthenticated: false, token: null, user: null });
  },

  updateUser: (updates) =>
    set((state) => ({
      user: state.user ? { ...state.user, ...updates } : null,
    })),
}));
